import React, { useCallback, useEffect, useRef, useState } from 'react';
import dayjs from 'dayjs';
import { AccordionDetails, Box, Button, Divider, Stack } from '@mui/material';
import { convertHtmlToText } from 'src/helpers/helpers';
import { RowAccordionSummary, SeamlessAccordion } from './SeamlessAccordion.styles';
import { SquareIconButton } from 'src/UIKit';
import { formatUnicodeStylesToSafeHtml } from 'src/components/TemplateText/helpers';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import { useSelector } from 'react-redux';
import useConfirmationDialog from 'src/components/ConfirmationDialog/view';
import { AppointmentType } from 'src/constants/types';
import { getCurrentCompanyId } from 'src/modules/companies/slices';
import {
  computeMessageReadOnly,
  useBuilderContext,
  useBuilderMetadata,
  useCampaignFormContext,
  useMessageValidity,
} from '../../context';
import { CampaignPolicy } from '../../policies';
import { CampaignConfigSchema } from '../../types';
import { FormMessage } from '../../types';
import {
  validateEnrollmentFollowUpTimes,
  validateMessageOrder,
} from '../../policies/shared/messageSchedule';
import { getAllowedMessageTypes, sectionDividerSx } from '../../helpers';
import { useCampaignBuilderV2Api } from '../../api';
import {
  canAddMessages,
  canRemoveMessage,
  getMessageTemplateForIndex,
  getScheduleLabel,
  isMessageDirty,
  newDefaultMessage,
} from '../../helpers/messageListHelpers';
import { useMessageTemplates } from '../../hooks';
import { MessageAccordionTitle } from './MessageAccordionTitle';
import { MessageForm } from '../MessageForm';
import { JoinWindowField } from './JoinWindowField';

interface MessagesFieldProps {
  policy: CampaignPolicy;
  config: CampaignConfigSchema;
}

export const MessagesField: React.FC<MessagesFieldProps> = ({ policy, config }) => {
  const { values, updateMessage, setMessages, isSubmitted } = useCampaignFormContext();
  const liveMessages: FormMessage[] = values.scheduledMessages ?? [];

  // Keyed by clientKey so it survives reorders.
  const [expandedClientKey, setExpandedClientKey] = useState<string | null>(null);

  /** Insert a message at its intended position and open it. */
  const addMessage = (message: FormMessage, afterIndex = liveMessages.length - 1) => {
    const insertAt = afterIndex + 1;
    const next = [...liveMessages];
    next.splice(insertAt, 0, message);
    setMessages(next.map((item, index) => ({ ...item, position: index + 1 })));
    setExpandedClientKey(message.clientKey);
  };

  const companyId = useSelector(getCurrentCompanyId) as string;
  const api = useCampaignBuilderV2Api();
  // Editors must not mount before their reference data resolves. TemplateText
  // strips merge tags it cannot match, and it builds its TipTap extensions once
  // — so the template list it is handed on mount is the one it keeps. `null`
  // means still loading, for both.
  const loadedMetadata = useBuilderMetadata();
  const metadata = loadedMetadata ?? [];
  const loadedTemplates = useMessageTemplates();
  const editorsReady = loadedMetadata !== null && loadedTemplates !== null;
  const [appointmentTypes, setAppointmentTypes] = useState<AppointmentType[]>([]);
  const [appointmentsLoading, setAppointmentsLoading] = useState(true);
  // Re-armed on setup: the ref survives Fast Refresh, so cleanup-only would latch it false.
  const mounted = useRef(true);
  useEffect(() => {
    mounted.current = true;
    return () => { mounted.current = false; };
  }, []);
  // Two editors can each trigger onRequestRefreshAppointments; the newer response wins.
  const requestGeneration = useRef(0);

  // Calendars for "Add Calendar Event". Reminders keep theirs at campaign level.
  const loadAppointmentTypes = useCallback(async () => {
    if (!companyId || !policy.supportsMessageAppointments()) {
      setAppointmentsLoading(false);
      return;
    }
    const generation = ++requestGeneration.current;
    const isCurrent = () => mounted.current && requestGeneration.current === generation;
    setAppointmentsLoading(true);
    try {
      const res = await api.getAppointmentTypes({
        companyId,
        limit: 10000,
        offset: 0,
        statuses: 'active,archived',
      });
      if (isCurrent()) setAppointmentTypes(res.data.items ?? []);
    } catch {
      // The control remains usable for selecting an existing event.
    } finally {
      // A superseded request leaves the flag alone: the one that replaced it owns
      // the spinner now.
      if (isCurrent()) setAppointmentsLoading(false);
    }
  }, [companyId, api, policy]);

  useEffect(() => { loadAppointmentTypes(); }, [loadAppointmentTypes]);

  // MessageCreditEstimates decides this while rendering, so it cannot be derived
  // from the form values; the primary action reads the collected verdict.
  const { report: reportValidity, forget: forgetValidity } = useMessageValidity();

  const { openDialog, ConfirmationDialog } = useConfirmationDialog();
  const { isCampaignReadOnly, campaignId } = useBuilderContext();

  const scheduleKind = policy.messageScheduleKind(config);
  const allowedMessageTypes = getAllowedMessageTypes(config);
  const isEnrollment = scheduleKind === 'enrollment';
  const isRecurring = scheduleKind === 'recurring';

  const handleAccordionChange = (clientKey: string) =>
    (event: React.SyntheticEvent) => {
      // Keeps the freshly opened message on screen in a long form.
      (event.currentTarget as HTMLElement).scrollIntoView({ behavior: 'smooth' });
      setExpandedClientKey(expandedClientKey === clientKey ? null : clientKey);
    };

  const handleRemove = (index: number) => {
    openDialog({
      title: 'Delete',
      content: 'Are you sure you want to delete this message?',
      onConfirm: () => {
        const removed = liveMessages[index];
        const remaining = liveMessages
          .filter((_, i) => i !== index)
          .map((msg, i) => (msg.position === i + 1 ? msg : { ...msg, position: i + 1 }));
        setMessages(remaining);
        if (removed) forgetValidity(removed.clientKey);
        if (liveMessages[index]?.clientKey === expandedClientKey) {
          setExpandedClientKey(remaining[0]?.clientKey ?? null);
        }
      },
    });
  };

  const handleAdd = (index: number, previous: FormMessage) => {
    const message = newDefaultMessage(policy, config, index + 2, previous);
    addMessage({
      ...message,
      ...(isEnrollment ? { enrollmentSection: previous.enrollmentSection ?? 'join' } : {}),
    }, index);
  };

  const hasFollowUpMessages = isEnrollment && liveMessages.some(m => m.enrollmentSection === 'scheduled');

  // A join template marked oneMessageOnly caps the campaign at the messages it
  // defines, so no follow-up may be added after it.
  const lastJoinIndex = isEnrollment
    ? liveMessages.reduce((last, m, i) => (m.enrollmentSection === 'scheduled' ? last : i), -1)
    : -1;
  // The section renders whenever an enrollment campaign has no follow-ups yet;
  // whether the button inside it is usable is a separate question. Old drew the
  // divider and a greyed-out button when the add was not allowed, so collapsing
  // both into one condition made the whole section vanish for a oneMessageOnly
  // join template instead of showing it disabled.
  const showFollowUpSection = isEnrollment && !hasFollowUpMessages;
  const canAddFollowUp = showFollowUpSection
    && lastJoinIndex >= 0
    && !getMessageTemplateForIndex(config.messageTemplates, lastJoinIndex)?.oneMessageOnly;

  const handleAddFollowUp = () => {
    const position = liveMessages.length + 1;
    const previous = liveMessages[liveMessages.length - 1];
    addMessage({
      ...newDefaultMessage(policy, config, position, previous),
      scheduledType: 'time',
      enrollmentSection: 'scheduled',
    });
  };

  // Disabled, never hidden: hiding made the control flicker as the user typed.
  const canAdd = canAddMessages(policy, config);

  const optOutText = values.optOutText ?? '';
  const isOptOutEnabled = values.isOptOutEnabled ?? false;
  const effectiveOptOutText = isOptOutEnabled ? optOutText : undefined;

  // Which message is out of order, and the earliest each picker may offer. Both
  // render inline — without them a bad order only disables the button, with
  // nothing on screen saying why.
  const scheduleAnchor = isEnrollment ? values.joinEndAt ?? null : values.dateScheduled ?? null;
  const messagesForScheduleValidation = isEnrollment
    ? liveMessages.filter(message => message.enrollmentSection === 'scheduled')
    : liveMessages;
  const { errorsByPosition, minTimeByPosition } = validateMessageOrder(
    messagesForScheduleValidation,
    scheduleAnchor,
  );
  const enrollmentTimeErrors = isEnrollment && isSubmitted
    ? validateEnrollmentFollowUpTimes(liveMessages, values.joinEndAt)
    : {};

  const firstScheduledIndex = isEnrollment
    ? liveMessages.findIndex(m => m.enrollmentSection === 'scheduled')
    : -1;

  if (!editorsReady) return null;

  return (
    <Stack spacing={0}>
      {liveMessages.map((msg, index) => {
        if (!msg) return null;

        // The delete button renders for every message after the first and is
        // merely disabled when that message is read-only.
        const isMessageReadOnly = computeMessageReadOnly({
          isCampaignReadOnly,
          campaignId,
          isEditable: msg.isEditable,
        });
        const showRemove = canRemoveMessage(msg.position);
        const configMessageTemplate = getMessageTemplateForIndex(config.messageTemplates, index);

        // The snippet renders as HTML so unicode bold/italic styling shows as
        // formatting rather than raw characters.
        const plainSnippet = convertHtmlToText(msg.text);
        const snippetHtml = plainSnippet.length
          ? formatUnicodeStylesToSafeHtml(plainSnippet)
          : 'Edit Message';
        const scheduleLabel = getScheduleLabel(msg, scheduleKind, index);

        const title = (
          <MessageAccordionTitle
            snippetHtml={snippetHtml}
            scheduleLabel={scheduleLabel}
            index={index}
            showRemove={showRemove}
            removeDisabled={isMessageReadOnly}
            onRemove={() => handleRemove(index)}
          />
        );

        const isEnrollmentFirst = isEnrollment && index === 0;
        const isFirstScheduledMsg = isEnrollment && index === firstScheduledIndex;
        // The add button is gated per message: hidden when that message is
        // read-only, for recurring campaigns, or when its template is oneMessageOnly.
        const shouldShowAddButton =
          canAdd
          && !isMessageReadOnly
          && !isRecurring
          && !configMessageTemplate?.oneMessageOnly;

        return (
          <div
            key={msg.clientKey}
            style={{
              textAlign: 'center',
              marginBottom: shouldShowAddButton ? undefined : '16px',
            }}
          >
            {/* Enrollment: "Join Messages" divider + JoinWindowField above first message */}
            {isEnrollmentFirst && (
              <>
                <Divider sx={sectionDividerSx}>
                  Join Messages
                </Divider>
                {/* No textAlign reset here on purpose: old let the join window
                    inherit the wrapper's `center`, which centres both picker
                    inputs and their helper text. Forcing left changed how the
                    row reads. */}
                <Box mt={-1.25} mb={2}>
                  <JoinWindowField />
                </Box>
              </>
            )}

            {/* Enrollment: "Follow Up Messages" divider before first scheduled message */}
            {isFirstScheduledMsg && (
              <Divider sx={sectionDividerSx}>
                Follow Up Messages
              </Divider>
            )}

            <SeamlessAccordion
              // A lone message is always expanded.
              expanded={expandedClientKey === msg.clientKey || liveMessages.length === 1}
              onChange={handleAccordionChange(msg.clientKey)}
            >
              <RowAccordionSummary
                style={{ overflow: 'hidden' }}
                expandIcon={liveMessages.length > 1 ? <ArrowDropDownIcon /> : undefined}
                id={`panel-header-${msg.clientKey}`}
                aria-controls={`panel-content-${msg.clientKey}`}
              >
                {title}
              </RowAccordionSummary>
              <AccordionDetails sx={{ textAlign: 'left', pt: 0 }}>
              <MessageForm
                message={msg}
                onChange={(partial) => updateMessage(index, partial)}
                scheduleKind={scheduleKind}
                position={msg.position}
                allowedMessageTypes={allowedMessageTypes}
                configMessageTemplate={configMessageTemplate}
                metadata={metadata}
                messageTemplates={loadedTemplates}
                optOutText={effectiveOptOutText}
                surveyEnabled
                captureEnabled
                disabled={isMessageReadOnly}
                appointmentTypes={appointmentTypes}
                appointmentsLoading={appointmentsLoading}
                onRequestRefreshAppointments={loadAppointmentTypes}
                isFirstEnrollmentScheduledMessage={isFirstScheduledMsg}
                onValidationChange={reportValidity}
                companyId={companyId}
                scheduleTimeError={enrollmentTimeErrors[msg.position] ?? errorsByPosition[msg.position]}
                minDateTime={
                  minTimeByPosition[msg.position]
                    ? dayjs(minTimeByPosition[msg.position] as string)
                    : null
                }
              />
              </AccordionDetails>
            </SeamlessAccordion>

            {/* Add button after THIS accordion, when applicable */}
            {shouldShowAddButton && (
              <SquareIconButton
                onClick={() => handleAdd(index, msg)}
                color="primary"
                // One button per message, so the label has to say which one —
                // matching MessageAccordionTitle's "Remove message N".
                aria-label={`Add message after ${index + 1}`}
                disabled={!isMessageDirty(msg)}
              >
                <AddCircleOutlineIcon style={{ transform: 'scale(0.8)' }} />
              </SquareIconButton>
            )}
          </div>
        );
      })}

      {/* Enrollment: "Add Follow Up Messages" CTA when no follow-up messages exist yet */}
      {showFollowUpSection && (
        <Box textAlign="center" mb={2}>
          <Divider sx={sectionDividerSx}>
            Follow Up Messages
          </Divider>
          {/* Hidden entirely when read-only; disabled — not hidden — when the
              join template caps the campaign at its own messages. */}
          {!isCampaignReadOnly && (
            <Button variant="outlined" disabled={!canAddFollowUp} onClick={handleAddFollowUp}>
              Add Follow Up Message(s)
            </Button>
          )}
        </Box>
      )}

      <ConfirmationDialog />
    </Stack>
  );
};
